'use client';

import { memo } from 'react';
import { MapPinIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { cn } from '@utils/cn';
import { Tags } from '@components/templates/my-jobs/Card/partials/Tags';

interface CompactJobCardProps {
  job: ApiSchema<'MyJobCard'>;
  isSelected?: boolean;
  onSelect?: (job: ApiSchema<'MyJobCard'>) => void;
  /** When provided, tag clicks call this instead of navigating to the standalone applicant page. */
  onTagClick?: (job: ApiSchema<'MyJobCard'>, status: number) => void;
  /** Optional href builder for tags — used in MC context to produce /[locale]/mc/... URLs. */
  buildTagHref?: (jobSlug: string, status: number) => string;
  /** Locale used to build the edit job URL. When provided, an Edit tag is shown. */
  locale?: Locale;
}

const getStatusDotClass = (active: boolean | undefined, expiresIn: number) => {
  if (!active) return 'bg-ink-light';
  return expiresIn > 7 ? 'bg-secondary' : 'bg-warning';
};

/**
 * @description Compact job card for the company jobs sidebar.
 * Shows status dot, job title, location, and applicant tags.
 */
const CompactJobCard = (props: CompactJobCardProps) => {
  const {
    job, isSelected, onSelect, onTagClick, buildTagHref, locale,
  } = props;

  const { t } = useTranslation('common');

  const handleClick = () => {
    onSelect?.(job);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        'cursor-pointer rounded-2xl border bg-surface p-3 transition-all duration-200',
        'hover:border-primary/30 hover:shadow-md',
        isSelected ? 'border-primary bg-primary/5' : 'border-input',
      )}
    >
      {/* Job title with status dot */}
      <Anchor
        styling="none"
        href={locale ? `/${locale}/mc/job/${job.slug}/applicants` : `/job/${job.slug}/applicants/flow`}
        className="flex items-start gap-2"
        onClick={(e) => {
          if (onSelect) {
            // In Mission Control context: load inline instead of navigating away
            e.preventDefault();
            onSelect(job);
          } else {
            e.stopPropagation();
          }
        }}
      >
        <span
          className={cn(
            'mt-1 size-2.5 shrink-0 rounded-full',
            getStatusDotClass(job.active, job.expiresIn),
          )}
        />
        <span className="line-clamp-2 text-sm font-semibold text-ink hover:text-primary">
          {job.title}
        </span>
      </Anchor>

      {/* Location - directly under title */}
      <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-medium">
        <MapPinIcon className="size-3.5 shrink-0" />
        <span className="truncate">{job.location}</span>
      </div>

      {/* Tags/Status list + Edit tag */}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <Tags
          job={job}
          useInkColor
          hideRecommended
          onTagClick={onTagClick ? (status) => onTagClick(job, status) : undefined}
          buildHref={buildTagHref}
        />

        {locale && job.slug && (
          <Anchor
            href={`/${locale}/mc/job-add/edit/${job.slug}`}
            styling="none"
            className="flex shrink-0 items-center gap-1 rounded-full border border-[#F0F2F4] bg-surface px-4 py-1.5 text-sm text-ink-medium hover:shadow-none"
            onClick={(e) => e.stopPropagation()}
          >
            <PencilIcon className="size-3.5" />
            <span>{t('my-jobs.card.actions.edit')}</span>
          </Anchor>
        )}
      </div>
    </div>
  );
};

export const MemoizedCompactJobCard = memo(CompactJobCard);
